import { memo, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import { lazyRenderWidgetByLoader } from 'core-components/helpers/lazyLoad'
import { ModalDialog } from 'core-components/library/modalDialog'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import MeasureController from 'core/analytics/MeasureController'

import { companyDetailsTranslations } from '../../translation'

import styles from './PartnerStatusModal.module.scss'

type Props = {
  partnerId: string
  isOpen: boolean
  onClose: () => void
}

const PARTNER_STATUS_VIEW_MODEL_ID = 'partner-status-hpscoutso'
const AMPLIFY_IMPACT_VIEW_MODEL_ID = 'hp-amplify-impact-summary'
const loadPartnerStatusWidget = () =>
  import('legacy/lut/components/widgets/public/partnerStatus/PartnerStatus')
const loadAmplifyImpactWidget = () =>
  import('legacy/lut/components/widgets/public/amplifyImpact/AmplifyImpact').then((m) => ({
    default: m.AmplifyImpact,
  }))

export const PartnerStatusModal = memo<Props>(({ partnerId, isOpen, onClose }) => {
  const user = useSelector(selectUser)
  const partnerStatusMetrics = useMemo(
    () => new MeasureController(PARTNER_STATUS_VIEW_MODEL_ID, { user }),
    [user]
  )
  const amplifyImpactMetrics = useMemo(
    () => new MeasureController(AMPLIFY_IMPACT_VIEW_MODEL_ID, { user }),
    [user]
  )
  const partnerStatusProps = useMemo(
    () => ({ pid: partnerId, metrics: partnerStatusMetrics }),
    [partnerId, partnerStatusMetrics]
  )
  const amplifyImpactProps = useMemo(
    () => ({ pid: partnerId, metrics: amplifyImpactMetrics }),
    [partnerId, amplifyImpactMetrics]
  )

  const renderContent = useCallback(
    () => (
      <div className={styles.content}>
        <section className={styles.panel}>
          <h3 className={styles.panelTitle}>
            <Translation text={companyDetailsTranslations.partnerStatusModal.partnerStatus} />
          </h3>
          {lazyRenderWidgetByLoader({
            id: PARTNER_STATUS_VIEW_MODEL_ID,
            loader: loadPartnerStatusWidget,
            props: partnerStatusProps,
            loadMeta: true,
          })}
        </section>
        <section className={styles.panel}>
          <h3 className={styles.panelTitle}>
            <Translation
              text={companyDetailsTranslations.partnerStatusModal.amplifyImpactSummary}
            />
          </h3>
          {lazyRenderWidgetByLoader({
            id: AMPLIFY_IMPACT_VIEW_MODEL_ID,
            loader: loadAmplifyImpactWidget,
            props: amplifyImpactProps,
          })}
        </section>
      </div>
    ),
    [partnerStatusProps, amplifyImpactProps]
  )

  return (
    <ModalDialog
      classes={styles}
      show={isOpen}
      onClose={onClose}
      content={renderContent}
      size="lg"
    />
  )
})

PartnerStatusModal.displayName = 'PartnerStatusModal'
